import { get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import { REVIEW_INTEREST_OPTIONS } from "./cityHubData"

export const REVIEW_REQUEST_FIRST_MAX = 500
export const REVIEW_REQUEST_WHY_MAX = 1000

export const REVIEW_WRITER_CTA = {
  become: "Become a Review Writer",
  edit: "Edit Request",
  write: "Write a Review",
}

const REQUEST_PAGE_SIZE = 50
const MAX_REQUEST_PAGES = 20

const sameId = (a, b) => a != null && b != null && String(a) === String(b)

const unwrapPagedList = result => {
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.data)) return result.data
  return []
}

const unwrapEntity = payload => {
  if (!payload || typeof payload !== "object") return null
  if (payload.id) return payload
  if (payload.data?.id) return payload.data
  if (Array.isArray(payload.data) && payload.data[0]?.id) return payload.data[0]
  return null
}

export const isAcceptedReviewWriterRequest = request => {
  const status = String(request?.status || "").toLowerCase()
  return status === "accepted" || status === "approved"
}

export const isPendingReviewWriterRequest = request => {
  const status = String(request?.status || "").toLowerCase()
  return (
    status === "pending" ||
    status === "submitted" ||
    status === "in_review" ||
    status === "awaiting_approval"
  )
}

export const isRejectedReviewWriterRequest = request => {
  const status = String(request?.status || "").toLowerCase()
  return status === "rejected" || status === "declined"
}

export const getReviewWriterCtaLabel = request => {
  if (!request?.id || isRejectedReviewWriterRequest(request)) {
    return REVIEW_WRITER_CTA.become
  }
  if (isAcceptedReviewWriterRequest(request)) return REVIEW_WRITER_CTA.write
  if (isPendingReviewWriterRequest(request)) return REVIEW_WRITER_CTA.edit
  return REVIEW_WRITER_CTA.become
}

export const pickCityReviewWriterRequest = (
  list,
  { cityId, userId } = {},
) => {
  const matches = (Array.isArray(list) ? list : []).filter(item => {
    if (!item?.id) return false
    if (
      cityId &&
      !sameId(item.city_id, cityId) &&
      !sameId(item.city?.id, cityId)
    ) {
      return false
    }
    if (userId) {
      const ownerId =
        item.user_id ||
        item.user?.id ||
        item.requester_id ||
        item.requester?.id
      if (ownerId && !sameId(ownerId, userId)) return false
    }
    return true
  })

  if (matches.length === 0) return null

  const accepted = matches.find(isAcceptedReviewWriterRequest)
  if (accepted) return accepted

  const pending = [...matches]
    .filter(isPendingReviewWriterRequest)
    .sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime()
      const bTime = new Date(b.created_at || 0).getTime()
      return bTime - aTime
    })[0]
  if (pending) return pending

  return null
}

const normalizeInterestValue = value => {
  const raw = String(value || "").trim()
  if (!raw) return ""
  const match = REVIEW_INTEREST_OPTIONS.find(
    option =>
      option.value === raw ||
      option.label === raw ||
      String(option.value).toLowerCase() === raw.toLowerCase() ||
      String(option.label).toLowerCase() === raw.toLowerCase(),
  )
  return match?.value || raw
}

export const mapReviewWriterRequestFormValues = request => ({
  what_to_review_first: String(request?.what_to_review_first || "").slice(
    0,
    REVIEW_REQUEST_FIRST_MAX,
  ),
  why_write_reviews: String(request?.why_write_reviews || "").slice(
    0,
    REVIEW_REQUEST_WHY_MAX,
  ),
  areas_of_interest: (
    Array.isArray(request?.areas_of_interest) ? request.areas_of_interest : []
  )
    .map(normalizeInterestValue)
    .filter(Boolean),
})

export const toReviewWriterRequestPayload = (
  values,
  { cityId, includeCityId } = {},
) => {
  const payload = {
    what_to_review_first: String(values?.what_to_review_first || "").trim(),
    why_write_reviews: String(values?.why_write_reviews || "").trim(),
    areas_of_interest: (
      Array.isArray(values?.areas_of_interest) ? values.areas_of_interest : []
    )
      .map(normalizeInterestValue)
      .filter(Boolean),
  }
  if (includeCityId) payload.city_id = cityId
  return payload
}

export const unwrapReviewWriterRequestResponse = response =>
  unwrapEntity(response?.data?.result) || unwrapEntity(response?.data)

export const fetchCityReviewWriterRequest = async ({
  cityId,
  userId,
  signal,
} = {}) => {
  if (!cityId) return { ok: true, request: null }

  let page = 1
  let lastPage = 1

  do {
    const response = await get(endpoints.getReviewWriterRequests, {
      params: {
        page,
        per_page: REQUEST_PAGE_SIZE,
        city_id: cityId,
        "with[]": ["city", "user"],
      },
      signal,
    })

    if (signal?.aborted) return { ok: false, aborted: true }

    if (!response) {
      return { ok: false, aborted: Boolean(signal?.aborted) }
    }

    if (!response?.data?.success) {
      toastApiError(response, "Failed to load review writer request")
      return { ok: false }
    }

    const result = response.data.result || {}
    const match = pickCityReviewWriterRequest(unwrapPagedList(result), {
      cityId,
      userId,
    })
    if (match) return { ok: true, request: match }

    lastPage = Number(result.last_page || 1)
    page += 1
  } while (page <= lastPage && page <= MAX_REQUEST_PAGES)

  return { ok: true, request: null }
}
